import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, Pin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sectionsForRole, PORTAL_SECTIONS, NAV_GROUPS, type PortalSection, type PortalSectionId, type NavGroupId } from '../lib/roles';
import { getPinnedSections, togglePinnedSection } from '../lib/pinnedSections';
import { LogoMark } from './LogoMark';

/**
 * Simply White's sidebar is pinned-by-default in the template's own dashboard
 * (`<body class="fixed-header dashboard menu-pin">`), so that's the first-run state here
 * too. The user's choice persists, because unpinning is a deliberate "give me the screen
 * width back" move that shouldn't reset on every navigation.
 */
const MENU_PIN_KEY = 'mediclynk.admin.menuPin';

function readMenuPin(): boolean {
  try {
    const stored = window.localStorage.getItem(MENU_PIN_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

/**
 * The template drives its layout entirely off body classes, and pages.js is what normally
 * sets them. This portal has no jQuery, so React owns them instead — same class names, so
 * every rule in the vendored theme applies unchanged.
 *
 * - `fixed-header` docks .header to the viewport top; .content's 60px top padding is what
 *   keeps the first row of content out from under it.
 * - `dashboard` paints the #f3f3f4 canvas behind .page-container.
 * - `menu-pin` shifts .page-sidebar fully into view (translate 210px, width 250px) and
 *   moves .content's left padding to 250px to make room.
 * - `sidebar-visible` is the hover-peek state: only the icon rail is on screen, and
 *   hovering slides the full 280px panel out over the content.
 * - `sidebar-open` is the tablet/phone drawer: below 991px the theme drops the rail
 *   entirely and instead slides .page-container 250px to the right.
 */
function useLayoutBodyClasses(pinned: boolean, peeking: boolean, drawerOpen: boolean) {
  useEffect(() => {
    document.body.classList.add('fixed-header', 'dashboard');
    return () => {
      document.body.classList.remove(
        'fixed-header',
        'dashboard',
        'menu-pin',
        'sidebar-visible',
        'sidebar-open',
      );
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-pin', pinned);
  }, [pinned]);

  // Peeking while pinned is meaningless — the panel is already fully out.
  useEffect(() => {
    document.body.classList.toggle('sidebar-visible', peeking && !pinned);
  }, [peeking, pinned]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', drawerOpen);
  }, [drawerOpen]);
}

interface UserMenuProps {
  name: string;
  email: string;
  role: string;
  onSignOut: () => void;
}

/**
 * The template's header profile control: name inline, then a circular avatar trigger with
 * a status dot (.profile-dropdown-toggle), opening a .dropdown-menu.profile-dropdown with
 * its "Signed in as" header and a divider. Bootstrap's JS isn't loaded, so React toggles
 * the `.show` class its CSS keys off instead — and the demo's dead links (Your Profile /
 * Activity / Archive / Features / Help / Settings) are left out, since none of them exist
 * in this portal.
 */
function UserMenu({ name, email, role, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const displayName = name || email;
  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join('') || '?';

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <div className="p-r-10 fs-14 d-lg-inline-block d-none m-l-20 user-menu-name">
        <span className="semi-bold">{displayName}</span>
      </div>
      <div className="dropdown" ref={rootRef}>
        <button
          className="profile-dropdown-toggle"
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label="Account menu"
        >
          <span className="thumbnail-wrapper d32 circular inline user-avatar">{initials}</span>
        </button>
        <div
          className={`dropdown-menu dropdown-menu-end profile-dropdown${open ? ' show' : ''}`}
          role="menu"
        >
          <span className="dropdown-item">
            <span>
              Signed in as <br />
              <b>{displayName}</b>
            </span>
          </span>
          <span className="dropdown-item fs-12 hint-text all-caps">{role}</span>
          <div className="dropdown-divider" />
          <button
            type="button"
            className="dropdown-item user-menu-signout"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
}

interface MenuItemProps {
  section: PortalSection;
  pinned: boolean;
  onTogglePin: (id: PortalSectionId) => void;
  /** Sub-menu rows get the template's smaller 30px icon-thumbnail. */
  nested?: boolean;
  className?: string;
}

/**
 * One `.menu-items > li` in the template's exact shape: the `<a>` carries only the label
 * (it's clipped to 65% width by the theme), and the icon lives in a sibling
 * `.icon-thumbnail` that floats right — which is the whole trick behind the collapsed
 * rail. At 70px wide, the only part of the 280px panel on screen is the strip the
 * thumbnails sit in, so the rail is "icons only" without a second set of markup.
 */
function MenuItem({ section, pinned, onTogglePin, nested = false, className = '' }: MenuItemProps) {
  const Icon = section.icon;
  /* Sections live inside groups, so that's where pinning is offered. A top-level row only
     gets the control when it's already pinned — i.e. it's a row in the Pinned block and
     the control is how you take it back out. */
  const showPin = nested || pinned;
  return (
    <li className={className}>
      <NavLink
        to={section.path}
        className={({ isActive }) => (isActive ? 'active' : '')}
        // `/mail` must not stay lit on `/mail/admin`: end-match any section that has a child section.
        end={section.path === '/' || PORTAL_SECTIONS.some((s) => s.path.startsWith(`${section.path}/`))}
        title={section.label}
      >
        <span className="title">{section.label}</span>
      </NavLink>
      <span className="icon-thumbnail">
        <Icon size={nested ? 15 : 18} />
      </span>
      {showPin && (
        <button
          type="button"
          className={pinned ? 'menu-pin-item pinned' : 'menu-pin-item'}
          aria-label={pinned ? `Unpin ${section.label}` : `Pin ${section.label}`}
          aria-pressed={pinned}
          onClick={() => onTogglePin(section.id)}
        >
          <Pin size={13} fill={pinned ? 'currentColor' : 'none'} />
        </button>
      )}
    </li>
  );
}

export function Layout() {
  const { portalUser, signOut } = useAuth();
  const [pinnedIds, setPinnedIds] = useState<PortalSectionId[]>(() => getPinnedSections());
  const [menuPin, setMenuPin] = useState<boolean>(readMenuPin);
  const [peeking, setPeeking] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<NavGroupId>>(() => new Set());
  const location = useLocation();

  useLayoutBodyClasses(menuPin, peeking, mobileNavOpen);

  useEffect(() => {
    setMobileNavOpen(false);
    setPeeking(false);
  }, [location.pathname]);

  const sections = useMemo(
    () =>
      portalUser ? sectionsForRole(portalUser.role, portalUser.permissions) : ([] as PortalSection[]),
    [portalUser],
  );

  /** The group holding the current route opens on its own, so a deep link never lands on a
   *  collapsed nav with no visible trace of where you are. */
  const activeGroup = useMemo(
    () => sections.find((section) => section.path === location.pathname)?.group,
    [sections, location.pathname],
  );

  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((prev) => (prev.has(activeGroup) ? prev : new Set(prev).add(activeGroup)));
  }, [activeGroup]);

  if (!portalUser) return null;

  const dashboardSection = sections.find((section) => section.id === 'dashboard');
  const pinnedSections = sections.filter(
    (section) => section.id !== 'dashboard' && pinnedIds.includes(section.id),
  );
  const currentSection = sections.find((section) => section.path === location.pathname);

  const handleTogglePin = (id: PortalSectionId) => setPinnedIds(togglePinnedSection(id));

  const toggleMenuPin = () => {
    setMenuPin((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(MENU_PIN_KEY, String(next));
      } catch {
        /* private browsing — the class still applies for this session */
      }
      return next;
    });
  };

  const toggleGroup = (id: NavGroupId) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      {/* BEGIN SIDEBAR */}
      <nav
        className={`page-sidebar${mobileNavOpen ? ' visible' : ''}`}
        data-pages="sidebar"
        onMouseEnter={() => setPeeking(true)}
        onMouseLeave={() => setPeeking(false)}
      >
        {/* BEGIN SIDEBAR MENU HEADER */}
        <div className="sidebar-header">
          <LogoMark size={22} />
          <span className="brand sidebar-brand-name">MedicLynkOS</span>
          <div className="sidebar-header-controls">
            <button
              type="button"
              className="btn btn-link d-none d-lg-inline-block"
              data-toggle-pin="sidebar"
              aria-pressed={menuPin}
              aria-label={menuPin ? 'Unpin navigation' : 'Pin navigation'}
              title={menuPin ? 'Unpin navigation' : 'Pin navigation'}
              onClick={toggleMenuPin}
            >
              <Pin size={16} fill={menuPin ? 'currentColor' : 'none'} />
            </button>
          </div>
        </div>
        {/* END SIDEBAR MENU HEADER */}

        {/* START SIDEBAR MENU — .sidebar-menu is height-capped at calc(100% - 50px) and
            ul.menu-items inside it carries `overflow: auto`, which is what gives the nav a
            scrollbar of its own. The panel is position:fixed, so this scroll is completely
            independent of the page scroll in .content — exactly as in the demo. */}
        <div className="sidebar-menu">
          <ul className="menu-items">
            {pinnedSections.length > 0 && (
              <>
                <li className="menu-section-title m-t-10">
                  <span className="title all-caps">Pinned</span>
                </li>
                {pinnedSections.map((section) => (
                  <MenuItem
                    key={`pinned-${section.id}`}
                    section={section}
                    pinned
                    onTogglePin={handleTogglePin}
                  />
                ))}
              </>
            )}

            {dashboardSection && (
              <MenuItem
                section={dashboardSection}
                pinned={pinnedIds.includes(dashboardSection.id)}
                onTogglePin={handleTogglePin}
                className={pinnedSections.length > 0 ? '' : 'm-t-10'}
              />
            )}

            {NAV_GROUPS.map((group) => {
              const groupSections = sections.filter((section) => section.group === group.id);
              if (groupSections.length === 0) return null;
              const open = openGroups.has(group.id);
              const GroupIcon = group.icon;
              /* `active` is what the theme keys `> ul.sub-menu { display: block }` off, so
                 it tracks expansion rather than "holds the current route" — the effect
                 above already expands whichever group owns the current path. */
              return (
                <li key={group.id} className={open ? 'open active' : ''}>
                  <a
                    href="#"
                    className="menu-group-toggle"
                    onClick={(event) => {
                      event.preventDefault();
                      toggleGroup(group.id);
                    }}
                    aria-expanded={open}
                    /* Collapsed to the icon rail the label is off-screen entirely, so the
                       tooltip is the only way to identify a group by its icon. */
                    title={group.label}
                  >
                    <span className="title">{group.label}</span>
                    <span className={open ? 'arrow open' : 'arrow'} />
                  </a>
                  <span className="icon-thumbnail">
                    <GroupIcon size={18} />
                  </span>
                  <ul className="sub-menu">
                    {groupSections.map((section) => (
                      <MenuItem
                        key={section.id}
                        section={section}
                        pinned={pinnedIds.includes(section.id)}
                        onTogglePin={handleTogglePin}
                        nested
                      />
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
          <div className="clearfix" />
        </div>
        {/* END SIDEBAR MENU */}
      </nav>
      {/* END SIDEBAR */}

      {mobileNavOpen && (
        <div
          className="sidebar-backdrop d-lg-none"
          role="presentation"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* START PAGE-CONTAINER */}
      <div className="page-container">
        {/* START HEADER */}
        <div className="header">
          <a
            href="#"
            className="btn-link toggle-sidebar d-lg-none btn-icon-link"
            aria-label="Open navigation menu"
            aria-expanded={mobileNavOpen}
            onClick={(event) => {
              event.preventDefault();
              setMobileNavOpen((value) => !value);
            }}
          >
            <Menu size={20} />
          </a>
          <div className="d-flex align-items-center">
            <div className="brand inline header-brand">
              <LogoMark size={20} />
              <span>MedicLynkOS</span>
            </div>
            <span className="d-none d-lg-inline-block header-section-label all-caps hint-text m-l-20">
              {currentSection?.label ?? 'Admin'}
            </span>
          </div>
          <div className="d-flex align-items-center">
            <UserMenu
              name={portalUser.name}
              email={portalUser.email}
              role={portalUser.role}
              onSignOut={() => void signOut()}
            />
          </div>
        </div>
        {/* END HEADER */}

        {/* START PAGE CONTENT WRAPPER */}
        <div className="page-content-wrapper">
          {/* START PAGE CONTENT */}
          <div className="content sm-gutter">
            <div className="jumbotron">
              <div className="container-fluid p-l-25 p-r-25 sm-p-l-0 sm-p-r-0">
                <div className="inner">
                  <ol className="breadcrumb sm-p-b-5">
                    <li className="breadcrumb-item">
                      <NavLink to="/">Home</NavLink>
                    </li>
                    {currentSection && currentSection.path !== '/' && (
                      <li className="breadcrumb-item active">{currentSection.label}</li>
                    )}
                  </ol>
                </div>
              </div>
            </div>
            {/* START CONTAINER FLUID */}
            <div className="container-fluid p-l-25 p-r-25 p-t-0 p-b-25 sm-padding-10">
              <Outlet />
            </div>
            {/* END CONTAINER FLUID */}
          </div>
          {/* END PAGE CONTENT */}
        </div>
        {/* END PAGE CONTENT WRAPPER */}
      </div>
      {/* END PAGE-CONTAINER */}
    </>
  );
}
